import React, { useState, useRef, useEffect } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { FaCheckCircle } from "react-icons/fa";
import { RxChevronDown, RxChevronUp } from "react-icons/rx";
import LearningComponentBot from "../learning/LearningComponentBot";
import moment from "moment";
import { formatCrypto, formatCurrency, getAssetName } from "../../utils";
import SkeletonCard from "../skeletons/SkeletonCard";
import BotLogo from "../commons/BotLogo";

const pieData = [
  { name: "Profit", value: 60.59, color: "#4CAF50" },
  { name: "Position", value: 76.27, color: "#FBC02D" },
  { name: "Order", value: 847.46, color: "#3F51B5" },
];

const BotOverviewXenoArc = ({ selectedBot, loading, setOpenDialog }) => {
  const netProfit =
    selectedBot?.assets?.profit !== 0
      ? selectedBot?.assets?.profit
      : (selectedBot?.realized_pnl || 0) + (selectedBot?.unrealized_pnl || 0);
  const roi =
    selectedBot?.capital > 0 ? (netProfit / selectedBot?.capital) * 100 : 0;
  const realizedPercentage =
    selectedBot?.capital > 0
      ? (selectedBot?.realized_pnl / selectedBot?.capital) * 100
      : 0;
  const unrealizedPercentage =
    selectedBot?.capital > 0
      ? (selectedBot?.unrealized_pnl / selectedBot?.capital) * 100
      : 0;

  // const start = moment(selectedBot?.updated_at);
  const start = moment(selectedBot?.created_at);

  // Format date like "21 Nov 2024, 19:20 (UTC+8)"
  const formattedDate =
    start.format("DD MMM YYYY, HH:mm") + ` (${start.format("z") || "UTC"})`;

  // Calculate duration from start until now
  const now = moment();
  const duration = moment.duration(now.diff(start)); // difference between now and start

  const days = Math.floor(duration.asDays()); // full days active
  const hours = duration.hours();

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex flex-col gap-5">
      <div className="p-4 bg-[#D3C4FF]/10 text-[#1F2937] text-sm rounded-xl">
        <div className="flex flex-col gap-4 w-full">
          <div className="">
            <div className="space-y-4">
              <div className="flex flex-row items-center gap-2 w-full">
                <div className="relative flex  -space-x-2">
                  {/* <img
                    src="images/icons/icon_bot_xenoarc.svg"
                    alt="bot icon"
                    className="w-6 h-6 rounded-full"
                  /> */}

                  <img
                    src={`/images/icons/icon_wallet_${selectedBot?.exchange_name.toLowerCase()}.svg`}
                    alt="binance"
                    className="w-6 h-6 rounded-full relative top-0 right-0"
                  />
                  <BotLogo
                    className="w-6 h-6 rounded-full"
                    classNameLogo="!h-[unset]"
                    botName={selectedBot?.strategy_name}
                  />
                </div>
                <p className="text-[#999999] leading-none text-sm md:text-[16px] capitalize flex">
                  Bot {selectedBot?.bot_status} since {formattedDate} ({days}d{" "}
                  {hours}h)
                  <span className="hidden md:block pl-2">
                    {selectedBot?.bot_status} {selectedBot?.coin_name}{" "}
                    {selectedBot?.strategy_name} {selectedBot?.exchange_name}
                  </span>
                </p>

                {/* ELIPSIS VERTICAL */}
                {/* Ikon Elipsis mepet kanan */}
                {/* <svg
                  onClick={() => setOpen((prev) => !prev)}
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 cursor-pointer ml-auto"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v.01M12 12v.01M12 18v.01"
                  />
                </svg> */}

                {/* Modal */}
                {open && (
                  <div
                    ref={dropdownRef}
                    className="absolute w-52 right-5 top-8 rounded-lg shadow-lg bg-white  z-10"
                  >
                    <ul className="text-sm text-gray-700 p-2">
                      <li>
                        <button className="w-full text-left bg-white hover:bg-gray-100 font-medium text-black">
                          View Trade History
                        </button>
                      </li>
                      <li>
                        <button className="w-full text-left bg-white hover:bg-gray-100 font-medium text-black">
                          View Bot Log
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              <div
                className="p-4 bg-[#F9FAFB] text-[#1F2937] text-sm rounded-xl "
                style={{ border: "1px solid #dde1e6" }}
              >
                <p className="font-bold text-xl">Investment</p>
                <div className="flex flex-col md:flex-row mt-3 md:mt-1">
                  <div className="w-1/2">
                    <p className="text-sm leading-none text-[#999999]">
                      Starting Value
                    </p>
                    <p className="flex items-center gap-2 font-bold [18px]  leading-none">
                      {loading ? (
                        <SkeletonCard
                          type="text"
                          lines={1}
                          widths={["40px"]}
                          heights={["10px"]}
                        />
                      ) : (
                        formatCurrency(selectedBot?.capital || 0)
                      )}{" "}
                      {selectedBot?.coin_name?.split("/")[1] || "USDT"}
                    </p>
                  </div>
                  <div className="w-1/2">
                    <p className="text-xs leading-none">Current Value</p>
                    <p className="flex items-center gap-2 font-bold text-[18px] leading-none !text-[#007A78]">
                      {loading ? (
                        <SkeletonCard
                          type="text"
                          lines={1}
                          widths={["40px"]}
                          heights={["10px"]}
                        />
                      ) : (
                        formatCurrency(selectedBot?.current_balance || 0)
                      )}{" "}
                      {selectedBot?.coin_name?.split("/")[1] || "USDT"}
                    </p>
                  </div>
                </div>
                {/* ROI */}
                <div className="flex flex-col md:flex-row md:items-center md:gap-2 mt-3 md:mt-1">
                  <p className="font-semibold text-2xl my-0">ROI</p>
                  <div className="flex flex-row items-center gap-3">
                    <p className="flex items-center gap-2 font-bold text-2xl my-0">
                      {loading ? (
                        <SkeletonCard
                          type="text"
                          lines={1}
                          widths={["60px"]}
                          heights={["10px"]}
                        />
                      ) : (
                        <>
                          {netProfit >= 0 ? "+" : ""}
                          {formatCurrency(netProfit || 0)}
                        </>
                      )}{" "}
                      {selectedBot?.coin_name?.split("/")[1] || "USDT"}
                    </p>
                    {!loading && (
                      <div className="md:text-right">
                        <span
                          className={`px-2 py-1 text-[14px] border ${
                            roi >= 0
                              ? "bg-green-500/10 !text-green-500 !border-green-500 "
                              : "bg-red-500/10 !text-red-500 !border-redn-500"
                          } rounded-md text-center whitespace-nowrap w-fit`}
                        >
                          {roi >= 0 ? "+" : ""}({formatCurrency(roi || 0)}%)
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                {/* Realized & Unrealized */}
                <div className="flex flex-col md:flex-row justify-between text-sm mt-3 md:mt-1">
                  {/* Realized */}
                  <div className="w-full md:w-1/2">
                    <p className="text-sm text-[#999999] leading-none">
                      Realized P/L
                    </p>
                    <p className="font-medium text-[18px] text-gray-800 leading-none">
                      {loading ? (
                        <SkeletonCard
                          type="text"
                          lines={1}
                          widths={["40px"]}
                          heights={["8px"]}
                        />
                      ) : (
                        <>
                          {selectedBot?.realized_pnl >= 0 ? "+" : ""}
                          {formatCurrency(selectedBot?.realized_pnl || 0)}{" "}
                          {selectedBot?.coin_name?.split("/")[1] || "USDT"}
                        </>
                      )}
                    </p>
                    {!loading && (
                      <p
                        className={`text-sm ${
                          realizedPercentage >= 0
                            ? "text-green-600"
                            : "text-red-600"
                        }  font-medium leading-none`}
                      >
                        {realizedPercentage >= 0 ? "+" : ""}
                        {formatCurrency(realizedPercentage || 0)}%
                      </p>
                    )}
                  </div>

                  {/* Unrealized */}
                  <div className="w-full md:w-1/2">
                    <p className="text-sm text-[#999999] leading-none">
                      Unrealized P/L
                    </p>
                    <p className="font-medium text-[18px] text-gray-800 leading-none">
                      {loading ? (
                        <div className="w-full flex items-end justify-end">
                          <SkeletonCard
                            type="text"
                            lines={1}
                            widths={["40px"]}
                            heights={["8px"]}
                          />
                        </div>
                      ) : (
                        <>
                          {selectedBot?.unrealized_pnl >= 0 ? "+" : ""}
                          {formatCurrency(
                            selectedBot?.unrealized_pnl || 0,
                          )}{" "}
                          {selectedBot?.coin_name?.split("/")[1] || "USDT"}
                        </>
                      )}
                    </p>
                    {!loading && (
                      <p
                        className={`text-sm ${
                          unrealizedPercentage >= 0
                            ? "text-green-600"
                            : "text-red-600"
                        }  font-medium leading-none`}
                      >
                        {unrealizedPercentage >= 0 ? "+" : ""}
                        {formatCurrency(unrealizedPercentage || 0)}%
                      </p>
                    )}
                  </div>
                </div>
                {/* CRYPTO WIN RATE SECTION */}
                {selectedBot?.winrate !== undefined && (
                  <div className="mt-4 flex items-center justify-between p-3 rounded-xl border border-[#dde1e6] bg-[#f8f9fa]">
                    <div className="flex items-center gap-3">
                      {/* Icon Container */}
                      <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                        <BotLogo
                          className="w-6 h-6 rounded-full"
                          classNameLogo="!h-[unset]"
                          botName={selectedBot?.strategy_name}
                        />
                      </div>
                      <div>
                        <p className="text-[12px] font-bold text-[#999999] uppercase tracking-wider leading-none mb-1">
                          Bot Accuracy
                        </p>
                        <p className="text-sm text-[#1F2937] font-medium leading-none">
                          Win Rate Performance
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      {loading ? (
                        <SkeletonCard
                          type="text"
                          lines={1}
                          widths={["50px"]}
                          heights={["20px"]}
                        />
                      ) : (
                        <span
                          className={`px-3 py-1 rounded-full font-bold text-lg ${
                            selectedBot?.winrate == null ||
                            selectedBot.winrate < 1
                              ? "bg-gray-100 text-gray-400"
                              : selectedBot.winrate >= 50
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                          }`}
                        >
                          {selectedBot?.winrate == null ||
                          selectedBot.winrate < 1
                            ? "-"
                            : `${selectedBot.winrate}%`}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {!loading && (
                <div className="flex gap-2 mt-4 justify-end">
                  <a
                    className="bg-transparent hover:bg-[#374151] hover:text-white ring-1 ring-black text-black px-5 py-2 text-center rounded-lg cursor-pointer w-1/2 md:w-fit"
                    onClick={() => setOpenDialog("step-bot-2-edit")}
                  >
                    Edit Settings
                  </a>
                  {selectedBot?.bot_status === "inactive" ? (
                    <a
                      className="bg-[#007A78] hover:bg-[#007A78]/90 text-white px-5 py-2 text-center rounded-lg cursor-pointer w-1/2 md:w-fit"
                      onClick={() => setOpenDialog("start-bot")}
                    >
                      Start Bot
                    </a>
                  ) : (
                    <a
                      className="bg-[#FF3B30] hover:bg-red-700 text-white px-5 py-2 text-center rounded-lg cursor-pointer w-1/2 md:w-fit"
                      onClick={() => setOpenDialog("stop-bot")}
                    >
                      Stop Bot
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="">
        <div className="bg-white rounded-xl p-6">
          <div className="flex flex-col md:flex-row items-center ">
            {/* Pie Chart */}
            {/* Value Boxes */}
            <div className="flex flex-col gap-1 w-full">
              {/* Profit */}{" "}
              <p className="font-bold text-xl mb-2">Allocation Asset</p>
              <div className="flex items-center rounded-md gap-2">
                <span className="w-1.5 h-6 rounded-md bg-[#4CAF50]" />
                <p className="text-base text-[#808080]/55 text-right">Profit</p>
                <p className="font-semibold text-base text-[#007A78]">
                  {loading ? (
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["40px"]}
                      heights={["16px"]}
                    />
                  ) : (
                    formatCurrency(selectedBot?.assets?.profit || 0) +
                    ` ${selectedBot?.coin_name?.split("/")[1] || "USDT"}`
                  )}
                </p>
              </div>
              {/* Position */}
              <div className="flex items-center rounded-md gap-2">
                <span className="w-1.5 h-6 rounded-md bg-[#FBC02D]" />
                <p className="text-base text-[#808080]/55 text-right">
                  Position
                </p>
                <p className="font-semibold text-base text-[#007A78]">
                  {loading ? (
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["45px"]}
                      heights={["16px"]}
                    />
                  ) : (
                    formatCrypto(selectedBot?.assets?.position || 0) +
                    ` ${getAssetName(selectedBot?.coin_name)}`
                  )}
                </p>
              </div>
              {/* Order */}
              <div className="flex items-center rounded-md gap-2">
                <span className="w-1.5 h-6 rounded-md bg-[#3F51B5]" />
                <p className="text-base text-[#808080]/55 text-right">Order</p>
                <p className="font-semibold text-base text-[#007A78]">
                  {loading ? (
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["35px"]}
                      heights={["16px"]}
                    />
                  ) : (
                    formatCurrency(selectedBot?.assets?.order || 0) +
                    ` ${selectedBot?.coin_name?.split("/")[1] || "USDT"}`
                  )}
                </p>
              </div>
            </div>
            <div className="mx-auto relative h-[200px] w-[220px]">
              <PieChart width={220} height={200}>
                <Pie
                  data={[
                    {
                      name: "Profit",
                      value: selectedBot?.assets?.profit,
                      color: "#4CAF50",
                    },
                    {
                      name: "Position",
                      value:
                        selectedBot?.assets?.position_usdt ||
                        selectedBot?.assets?.position,
                      color: "#FBC02D",
                    },
                    {
                      name: "Order",
                      value: selectedBot?.assets?.order,
                      color: "#3F51B5",
                    },
                  ]}
                  dataKey="value"
                  outerRadius={80}
                  innerRadius={55}
                  paddingAngle={2}
                  startAngle={90}
                  endAngle={-270}
                >
                  {[
                    {
                      name: "Profit",
                      value: selectedBot?.assets?.profit,
                      color: "#4CAF50",
                    },
                    {
                      name: "Position",
                      value:
                        selectedBot?.assets?.position_usdt ||
                        selectedBot?.assets?.position,
                      color: "#FBC02D",
                    },
                    {
                      name: "Order",
                      value: selectedBot?.assets?.order,
                      color: "#3F51B5",
                    },
                  ].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
              {/* Center Label */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                <p className="text-xs text-[#999999]">Asset</p>
                <p className="font-bold text-lg">
                  {loading ? (
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["35px"]}
                      heights={["16px"]}
                    />
                  ) : (
                    formatCurrency(selectedBot?.assets?.asset || 0)
                  )}
                </p>
                <p className="text-xs font-semibold text-gray-700">
                  {selectedBot?.coin_name?.split("/")[1] || "USDT"}
                </p>
              </div>
            </div>
          </div>
          <div
            className="bg-white rounded-xl p-4"
            style={{ border: "1px solid #dde1e6" }}
          >
            {/* grid */}
            {selectedBot?.strategy_name?.toLowerCase() === "grid" && (
              <div className="grid grid-cols-1 [@media(min-width:400px)]:grid-cols-2 xl:!grid-cols-4 text-left text-sm text-gray-900">
                {/* Row 1 */}
                <div className="flex flex-col pt-1">
                  <p className="text-[14px] text-[#999999] leading-[12px]">
                    Capital
                  </p>
                  <p className="text-[18px] leading-[12px] text-[#21272A]">
                    {loading ? (
                      <SkeletonCard
                        type="text"
                        lines={1}
                        widths={["60px"]}
                        heights={["10px"]}
                      />
                    ) : (
                      formatCurrency(selectedBot?.setting?.amount || 0)
                    )}
                    &nbsp;{selectedBot?.coin_name?.split("/")[1] || "USDT"}
                  </p>
                  <p className="text-[14px] text-[#999999] leading-[3px]">
                    Buy width
                    <strong className="!font-semibold text-black text-sm leading-[3px] ml-1">
                      {loading ? (
                        <SkeletonCard
                          type="text"
                          lines={1}
                          widths={["60px"]}
                          heights={["10px"]}
                        />
                      ) : (
                        formatCurrency(selectedBot?.setting?.grid_width || 0)
                      )}
                      &nbsp;%
                    </strong>
                  </p>
                </div>

                <div className="flex flex-col pt-1">
                  <p className="text-[14px] text-[#999999] leading-[12px]">
                    Trade size
                  </p>
                  <p className="text-[18px] leading-[12px] text-[#21272A]">
                    {loading ? (
                      <SkeletonCard
                        type="text"
                        lines={1}
                        widths={["60px"]}
                        heights={["10px"]}
                      />
                    ) : (
                      formatCurrency(selectedBot?.setting?.trade_size || 0)
                    )}
                    &nbsp;{selectedBot?.coin_name?.split("/")[1] || "USDT"}
                  </p>
                  <p className="text-[14px] text-[#999999] leading-[3px]">
                    Sell width{" "}
                    <strong className="!font-semibold text-black text-sm leading-[3px] ml-1">
                      {loading ? (
                        <SkeletonCard
                          type="text"
                          lines={1}
                          widths={["60px"]}
                          heights={["10px"]}
                        />
                      ) : (
                        formatCurrency(
                          selectedBot?.setting?.grid_width_exit || 0,
                        )
                      )}
                      &nbsp;%
                    </strong>
                  </p>
                  {/* <p className="text-[14px] text-[#999999] leading-[3px]">
                    Buy after bounce
                    <strong className="!font-semibold text-black text-sm leading-[3px] ml-1">
                      {loading ? (
                        <SkeletonCard
                          type="text"
                          lines={1}
                          widths={["60px"]}
                          heights={["10px"]}
                        />
                      ) : (
                        formatCurrency(
                          selectedBot?.setting?.buy_after_bounce || 0
                        )
                      )}
                      &nbsp;%
                    </strong>
                  </p> */}
                </div>

                <div className="flex flex-col pt-1">
                  <p className="text-[14px] text-[#999999] leading-[12px]">
                    Number of steps
                  </p>
                  <p className="text-[18px] leading-[12px] text-[#21272A]">
                    {loading ? (
                      <SkeletonCard
                        type="text"
                        lines={1}
                        widths={["60px"]}
                        heights={["10px"]}
                      />
                    ) : (
                      formatCurrency(selectedBot?.setting?.number_of_grids || 0)
                    )}
                    &nbsp;
                  </p>
                </div>

                <div className="flex flex-col pt-1">
                  <p className="text-[14px] text-[#999999] leading-[12px]">
                    Start price
                  </p>
                  <p className="text-[18px] leading-[12px] text-[#21272A]">
                    {loading ? (
                      <SkeletonCard
                        type="text"
                        lines={1}
                        widths={["60px"]}
                        heights={["10px"]}
                      />
                    ) : (
                      formatCurrency(selectedBot?.setting?.trade_size || 0)
                    )}
                    &nbsp;{selectedBot?.coin_name?.split("/")[1] || "USDT"}
                  </p>
                  {/* <p className="text-xs text-[#999999] leading-[3px]">
                    Sell after bounce{" "}
                    <strong className="!font-semibold text-black text-sm leading-[3px] ml-1">
                      {loading ? (
                        <SkeletonCard
                          type="text"
                          lines={1}
                          widths={["60px"]}
                          heights={["10px"]}
                        />
                      ) : (
                        formatCurrency(
                          selectedBot?.setting?.sell_after_bounce || 0
                        )
                      )}
                      &nbsp;%
                    </strong>
                  </p> */}
                </div>
              </div>
            )}
            {/* end grid */}

            {/* xenoarc */}
            {selectedBot?.strategy_name?.toLowerCase() === "xeno arc" && (
              <div className="grid grid-cols-1 [@media(min-width:400px)]:grid-cols-2 xl:!grid-cols-4 text-left text-sm text-gray-900">
                {/* Row 1 */}
                <div className="flex flex-col pt-1">
                  <p className="text-[14px] text-[#999999] leading-[12px]">
                    Capital
                  </p>
                  <p className="text-[18px] leading-[12px] text-[#21272A]">
                    {loading ? (
                      <SkeletonCard
                        type="text"
                        lines={1}
                        widths={["60px"]}
                        heights={["10px"]}
                      />
                    ) : (
                      formatCurrency(selectedBot?.setting?.capital || 0)
                    )}
                    &nbsp;{selectedBot?.coin_name?.split("/")[1] || "USDT"}
                  </p>
                </div>
                <div className="flex flex-col pt-1">
                  <p className="text-[14px] text-[#999999] leading-[12px]">
                    Trade size
                  </p>
                  <p className="text-[18px] leading-[12px] text-[#21272A]">
                    {loading ? (
                      <SkeletonCard
                        type="text"
                        lines={1}
                        widths={["60px"]}
                        heights={["10px"]}
                      />
                    ) : (
                      formatCurrency(selectedBot?.setting?.amount || 0)
                    )}
                    &nbsp;{selectedBot?.coin_name?.split("/")[1] || "USDT"}
                  </p>
                </div>

                <div className="flex flex-col pt-1">
                  <p className="text-[14px] text-[#999999] leading-[12px]">
                    Number of steps
                  </p>
                  <p className="text-[18px] leading-[12px] text-[#21272A]">
                    {loading ? (
                      <SkeletonCard
                        type="text"
                        lines={1}
                        widths={["60px"]}
                        heights={["10px"]}
                      />
                    ) : (
                      formatCurrency(selectedBot?.setting?.number_of_grids || 0)
                    )}
                    &nbsp;
                  </p>
                </div>

                <div className="flex flex-col pt-1">
                  {/* <p className="text-[14px] text-[#999999] leading-[12px]">
                    Start price
                  </p>
                  <p className="text-[18px] leading-[12px] text-[#21272A]">
                    {loading ? (
                      <SkeletonCard
                        type="text"
                        lines={1}
                        widths={["60px"]}
                        heights={["10px"]}
                      />
                    ) : (
                      formatCurrency(selectedBot?.setting?.trade_size || 0)
                    )}
                    &nbsp;USDT
                  </p> */}
                </div>

                {/* Row 2 */}
                <div className="flex flex-col pt-1">
                  <p className="text-[14px] text-[#999999] leading-[3px]">
                    Buy width
                    <strong className="!font-semibold text-black text-sm leading-[3px] ml-1">
                      {loading ? (
                        <SkeletonCard
                          type="text"
                          lines={1}
                          widths={["60px"]}
                          heights={["10px"]}
                        />
                      ) : (
                        formatCurrency(selectedBot?.setting?.grid_width || 0)
                      )}
                      &nbsp;%
                    </strong>
                  </p>
                </div>
                <div className="flex flex-col pt-1">
                  <p className="text-[14px] text-[#999999] leading-[3px]">
                    Buy flag return
                    <strong className="!font-semibold text-black text-sm leading-[3px] ml-1">
                      {loading ? (
                        <SkeletonCard
                          type="text"
                          lines={1}
                          widths={["60px"]}
                          heights={["10px"]}
                        />
                      ) : (
                        formatCurrency(
                          selectedBot?.setting?.buy_flg_return || 0,
                        )
                      )}
                      &nbsp;%
                    </strong>
                  </p>
                </div>
                <div className="flex flex-col pt-1">
                  <p className="text-[14px] text-[#999999] leading-[3px]">
                    Sell width{" "}
                    <strong className="!font-semibold text-black text-sm leading-[3px] ml-1">
                      {loading ? (
                        <SkeletonCard
                          type="text"
                          lines={1}
                          widths={["60px"]}
                          heights={["10px"]}
                        />
                      ) : (
                        formatCurrency(
                          selectedBot?.setting?.grid_width_exit || 0,
                        )
                      )}
                      &nbsp;%
                    </strong>
                  </p>
                </div>
                <div className="flex flex-col pt-1">
                  <p className="text-xs text-[#999999] leading-[3px]">
                    Sell flag return{" "}
                    <strong className="!font-semibold text-black text-sm leading-[3px] ml-1">
                      {loading ? (
                        <SkeletonCard
                          type="text"
                          lines={1}
                          widths={["60px"]}
                          heights={["10px"]}
                        />
                      ) : (
                        formatCurrency(
                          selectedBot?.setting?.exit_flg_return || 0,
                        )
                      )}
                      &nbsp;%
                    </strong>
                  </p>
                </div>
              </div>
            )}
            {/* end xenoarc */}
            {/* xeno bot v3 */}
            {selectedBot?.strategy_name?.toLowerCase() === "xeno bot v3" && (
              <div className="w-full py-3 flex justify-center  items-center text-left text-sm text-gray-400">
                <div>No data</div>
              </div>
            )}
            {/* end xeno bot v3 */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BotOverviewXenoArc;
